/**
 * ViewportControl component - Development tool for testing mobile viewports
 * Only visible in development mode. Hidden when Playwright drives the page
 * (`navigator.webdriver`) so the FAB cannot intercept the Profile tab.
 */

import { useEffect, useState } from "react";

/**
 * Device preset configurations
 */
const DEVICE_PRESETS = [
  { name: "iPhone SE", width: 375, height: 667 },
  { name: "iPhone 12/13/14", width: 390, height: 844 },
  { name: "iPhone 15/15 Pro", width: 393, height: 852 },
  { name: "iPhone 15 Pro Max", width: 430, height: 932 },
  { name: "Galaxy S24", width: 360, height: 780 },
  { name: "Galaxy S24 Ultra", width: 412, height: 915 },
  { name: "Pixel 7", width: 412, height: 915 },
  { name: "iPad Mini", width: 768, height: 1024 },
  { name: "iPad Pro", width: 1024, height: 1366 },
  { name: "Desktop", width: 1920, height: 1080 },
] as const;

/**
 * ViewportControl component for development viewport testing
 */
export function ViewportControl() {
  const [isOpen, setIsOpen] = useState(false);
  const [currentViewport, setCurrentViewport] = useState({ width: window.innerWidth, height: window.innerHeight });
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    if (!import.meta.env.DEV) {
      return;
    }

    const updateViewport = () => {
      setCurrentViewport({ width: window.innerWidth, height: window.innerHeight });
    };

    window.addEventListener("resize", updateViewport);
    return () => window.removeEventListener("resize", updateViewport);
  }, []);

  const applyViewport = (width: number, height: number) => {
    const root = document.documentElement;
    const body = document.body;

    root.style.setProperty("--viewport-width", `${width}px`);
    root.style.setProperty("--viewport-height", `${height}px`);

    root.style.width = `${width}px`;
    root.style.height = `${height}px`;
    root.style.maxWidth = `${width}px`;
    root.style.maxHeight = `${height}px`;
    root.style.margin = "0 auto";
    root.style.position = "relative";
    root.style.overflowX = "hidden";

    body.style.width = `${width}px`;
    body.style.minHeight = `${height}px`;
    body.style.maxWidth = `${width}px`;
    body.style.margin = "0 auto";
    body.style.position = "relative";
    body.style.overflowX = "hidden";

    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.width = "100%";
      rootElement.style.minHeight = "100%";
      rootElement.style.maxWidth = `${width}px`;
      rootElement.style.margin = "0 auto";
      rootElement.style.position = "relative";
    }

    const style = document.createElement("style");
    style.id = "viewport-constraint-styles";
    style.textContent = `
      html[style*="--viewport-width"] {
        width: var(--viewport-width) !important;
        max-width: var(--viewport-width) !important;
        overflow-x: hidden !important;
      }
      html[style*="--viewport-width"] body {
        width: var(--viewport-width) !important;
        max-width: var(--viewport-width) !important;
        overflow-x: hidden !important;
      }
      html[style*="--viewport-width"] #root {
        width: var(--viewport-width) !important;
        max-width: var(--viewport-width) !important;
        overflow-x: hidden !important;
        position: relative !important;
      }
      /* Full-width horizontal fixed bars (bottom nav, top app bar): need explicit width — otherwise shrink-to-fit misaligns under left: 50% + translateX. */
      html[style*="--viewport-width"] [class*="fixed"][class*="left-0"][class*="right-0"],
      html[style*="--viewport-width"] [class*="fixed"][class*="inset-x-0"] {
        width: var(--viewport-width) !important;
        max-width: var(--viewport-width) !important;
        left: 50% !important;
        transform: translateX(-50%) !important;
        right: auto !important;
        box-sizing: border-box !important;
      }
      html[style*="--viewport-width"] [class*="fixed"][class*="inset-0"] {
        width: var(--viewport-width) !important;
        max-width: var(--viewport-width) !important;
        left: 50% !important;
        transform: translateX(-50%) !important;
        right: auto !important;
      }
      html[style*="--viewport-width"] [class*="fixed"][class*="right-"]:not([class*="left-"]) {
        right: calc(50% - var(--viewport-width) / 2) !important;
        transform: translateX(0) !important;
      }
    `;
    const existingStyle = document.getElementById("viewport-constraint-styles");
    if (existingStyle) {
      existingStyle.remove();
    }
    document.head.appendChild(style);

    setCurrentViewport({ width, height });
    setIsActive(true);
  };

  const resetViewport = () => {
    const root = document.documentElement;
    const body = document.body;

    root.style.removeProperty("--viewport-width");
    root.style.removeProperty("--viewport-height");
    root.style.width = "";
    root.style.height = "";
    root.style.maxWidth = "";
    root.style.maxHeight = "";
    root.style.margin = "";
    root.style.position = "";
    root.style.overflowX = "";

    body.style.width = "";
    body.style.minHeight = "";
    body.style.maxWidth = "";
    body.style.margin = "";
    body.style.position = "";
    body.style.overflowX = "";

    const rootElement = document.getElementById("root");
    if (rootElement) {
      rootElement.style.width = "";
      rootElement.style.minHeight = "";
      rootElement.style.maxWidth = "";
      rootElement.style.margin = "";
      rootElement.style.position = "";
    }

    const style = document.getElementById("viewport-constraint-styles");
    if (style) {
      style.remove();
    }

    setCurrentViewport({ width: window.innerWidth, height: window.innerHeight });
    setIsActive(false);
  };

  if (!import.meta.env.DEV || navigator.webdriver) {
    return null;
  }

  return (
    <>
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="fixed right-4 z-[9999] bottom-[calc(var(--height-bottom-tab-bar)+1rem)] bg-pink-600 text-white p-3 rounded-full shadow-lg hover:bg-pink-700 transition-colors"
        data-testid="viewport-control-toggle"
        aria-label="Toggle viewport control"
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          className="h-6 w-6"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
        >
          <title>Viewport Control</title>
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z"
          />
        </svg>
      </button>

      {isOpen && (
        <div
          className="fixed right-4 z-[9999] bottom-[calc(var(--height-bottom-tab-bar)+5rem)] bg-white rounded-lg shadow-2xl border border-gray-200 p-4 w-80 max-h-[80vh] overflow-y-auto"
          data-testid="viewport-control-panel"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Viewport Control</h3>
            <button
              type="button"
              onClick={() => setIsOpen(false)}
              className="text-gray-400 hover:text-gray-600 transition-colors"
              aria-label="Close viewport control"
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className="h-5 w-5"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <title>Close</title>
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          <div className="mb-4 p-2 bg-gray-50 rounded text-sm">
            <div className="text-gray-600">Current:</div>
            <div className="font-mono text-gray-900">
              {currentViewport.width} × {currentViewport.height}
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {DEVICE_PRESETS.map((device) => (
              <button
                key={device.name}
                type="button"
                onClick={() => applyViewport(device.width, device.height)}
                className="w-full text-left px-3 py-2 rounded border border-gray-200 hover:bg-gray-50 hover:border-pink-500 transition-colors"
                data-testid={`viewport-preset-${device.name.toLowerCase().replace(/\s+/g, "-")}`}
              >
                <div className="font-medium text-gray-900">{device.name}</div>
                <div className="text-xs text-gray-500 font-mono">
                  {device.width} × {device.height}
                </div>
              </button>
            ))}
          </div>

          {isActive && (
            <button
              type="button"
              onClick={resetViewport}
              className="w-full px-4 py-2 bg-gray-200 text-gray-900 rounded hover:bg-gray-300 transition-colors font-medium"
              data-testid="viewport-reset"
            >
              Reset to Full Screen
            </button>
          )}
        </div>
      )}
    </>
  );
}
